import React from 'react';
import {
  InfoCard,
  InfoCardVariants,
  Link,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Box,
  Chip,
  Grid,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  getEntitySourceLocation,
  parseEntityRef,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';

/**
 * Converts an entity reference (kind:namespace/name) to a catalog URL path
 * URL format: /catalog/:namespace/:kind/:name
 */
const entityRefToPath = (entityRef: string): string => {
  try {
    const parsed = parseEntityRef(entityRef);
    return `/catalog/${parsed.namespace || 'default'}/${parsed.kind.toLowerCase()}/${parsed.name}`;
  } catch {
    // Fallback for malformed refs
    return `/catalog/${entityRef.replace(':', '/')}`;
  }
};

const useStyles = makeStyles(theme => ({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)',
    marginBottom: '10px',
  },
  label: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: '0.65rem',
    fontWeight: 'bold',
    letterSpacing: 0.5,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  value: {
    fontWeight: 'bold',
    overflow: 'hidden',
    lineHeight: '24px',
    wordBreak: 'break-word',
  },
  description: {
    wordBreak: 'break-word',
  },
}));

/**
 * Custom AboutCard that displays "Ring" instead of "Lifecycle"
 * to match PhysicsX organization terminology
 */
export const CustomAboutCard = (props: {
  variant?: InfoCardVariants;
}) => {
  const { variant } = props;
  const classes = useStyles();
  const { entity } = useEntity();

  const entityMetadata = entity.metadata;
  const entitySpec = entity.spec as {
    type?: string;
    lifecycle?: string;
    owner?: string;
    system?: string;
  } | undefined;

  // Get owner from relations
  const ownedByRelations = entity.relations?.filter(
    r => r.type === RELATION_OWNED_BY,
  ) || [];
  const owner = ownedByRelations.length > 0 
    ? ownedByRelations[0].targetRef 
    : entitySpec?.owner;

  // Get system/stamp from relations
  // Prefer systems that match the stamp pattern (plat-*) over namespace-based systems
  const partOfRelations = entity.relations?.filter(
    r => r.type === RELATION_PART_OF && r.targetRef.startsWith('system:'),
  ) || [];
  
  // Find a stamp (plat-* pattern) first, otherwise use the first system
  const stampRelation = partOfRelations.find(r => {
    const name = r.targetRef.split('/').pop() || '';
    return name.startsWith('plat-');
  });
  const system = stampRelation?.targetRef 
    || (partOfRelations.length > 0 ? partOfRelations[0].targetRef : undefined)
    || entitySpec?.system;

  // Get tenant component from partOf relations (component: refs, not system:)
  const tenantComponentRelations = entity.relations?.filter(
    r => r.type === RELATION_PART_OF && r.targetRef.startsWith('component:'),
  ) || [];
  const tenantComponent = tenantComponentRelations.length > 0 
    ? tenantComponentRelations[0].targetRef 
    : undefined;
  const version = entityMetadata.annotations?.['physicsx.ai/version'];

  // Get source location
  let sourceLocation: string | undefined;
  try {
    const location = getEntitySourceLocation(entity);
    sourceLocation = location?.target;
  } catch {
    sourceLocation = undefined;
  }

  const AboutField = ({ label, value, gridSizes = { xs: 12, sm: 6, lg: 4 } }: {
    label: string;
    value?: string | React.ReactNode;
    gridSizes?: { xs?: 12 | 6 | 4 | 3; sm?: 12 | 6 | 4 | 3; lg?: 12 | 6 | 4 | 3 };
  }) => {
    if (!value) return null;
    return (
      <Grid item xs={gridSizes.xs} sm={gridSizes.sm} lg={gridSizes.lg}>
        <Typography variant="subtitle2" className={classes.label}>
          {label}
        </Typography>
        <Typography variant="body2" className={classes.value}>
          {value}
        </Typography>
      </Grid>
    );
  };

  return (
    <InfoCard
      title="About"
      variant={variant}
      className={variant === 'gridItem' ? classes.gridItemCard : undefined}
    >
      <Grid container>
        {/* Description */}
        {entityMetadata.description && (
          <Grid item xs={12}>
            <Typography variant="body2" className={classes.description} paragraph>
              {entityMetadata.description}
            </Typography>
          </Grid>
        )}

        {/* Owner */}
        <AboutField 
          label="Owner" 
          value={owner ? (
            <Link to={entityRefToPath(owner)}>
              {owner.split('/').pop() || owner}
            </Link>
          ) : undefined} 
        />

        {/* Type */}
        <AboutField 
          label="Type" 
          value={entitySpec?.type ? (
            <Chip size="small" label={entitySpec.type} />
          ) : undefined} 
        />

        {/* Ring (formerly Lifecycle) */}
        <AboutField 
          label="Ring" 
          value={entitySpec?.lifecycle ? (
            <Chip size="small" label={entitySpec.lifecycle} />
          ) : undefined} 
        />

        {/* Stamp (formerly System) */}
        <AboutField 
          label="Stamp" 
          value={system ? (
            <Link to={entityRefToPath(system)}>
              {system.split('/').pop() || system}
            </Link>
          ) : undefined} 
        />

        {/* Tenant (formerly Namespace) - links to tenant component if available */}
        {entityMetadata.namespace && entityMetadata.namespace !== 'default' && (
          <AboutField 
            label="Tenant" 
            value={tenantComponent ? (
              <Link to={entityRefToPath(tenantComponent)}>
                {entityMetadata.namespace}
              </Link>
            ) : entityMetadata.namespace} 
          />
        )}

        {/* Version */}
        <AboutField
          label="Version"
          value={version ? (
            <Tooltip title={version} arrow>
              <span>{version.slice(0, 7)}</span>
            </Tooltip>
          ) : undefined}
        />

        {/* Tags */}
        {entityMetadata.tags && entityMetadata.tags.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" className={classes.label}>
              Tags
            </Typography>
            <Box>
              {entityMetadata.tags.map(tag => (
                <Chip
                  key={tag}
                  size="small"
                  label={tag}
                  style={{ marginRight: 4, marginBottom: 4 }}
                />
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </InfoCard>
  );
};
